import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ShiftApiViewController from "../../redux/actions/shiftApiAction";
import AddAssignShiftForm from "./AddAssignShiftForm";
import ExportButton from "../../components/ag grid/ExportButton";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const AssignShiftList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const { hasPermission } = usePermission();

  const [showModal, setShowModal] = useState(false);
  const [editAssignment, setEditAssignment] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const { getEmployeeShifts, deleteAssignedShift } = ShiftApiViewController();
  const { employeeShiftsData, loading } = useSelector((state) => state.shifts);

  const canCreate = hasPermission("assign_shift", "create");
  const canEdit = hasPermission("assign_shift", "edit");
  const canDelete = hasPermission("assign_shift", "delete");

  useEffect(() => {
    dispatch(getEmployeeShifts());
  }, [dispatch]);

  const handleDelete = useCallback(
    (id) => dispatch(deleteAssignedShift(id)),
    [dispatch, deleteAssignedShift]
  );

  const handleEdit = useCallback((assignment) => {
    setEditAssignment(assignment);
    setShowModal(true);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee Name",
        valueGetter: (params) => {
          const emp = params.data.employee_details;
          return emp ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim() : "-";
        },
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: "Shift Name",
        valueGetter: (params) => params.data.shift_details?.shift_name || "-",
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: "Start Date",
        field: "start_date",
        width: 120,
      },
      {
        headerName: "End Date",
        field: "end_date",
        width: 120,
        valueFormatter: (params) => params.value ? params.value : "Ongoing",
      },
      ...(canEdit || canDelete
        ? [
            {
              headerName: "Actions",
              field: "actions",
              pinned: "right",
              width: 120,
              minWidth: 120,
              maxWidth: 120,
              suppressSizeToFit: true,
              sortable: false,
              cellRenderer: (params) => (
                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(params.data)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDelete(params.data.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEdit, canDelete, handleEdit, handleDelete]
  );

  return (
    <ListPageLayout
      title="Assign Shifts"
      subtitle="Assign shifts to employees"
      headerAction={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => {
              setEditAssignment(null);
              setShowModal(true);
            }}
            sx={toolbarButtonSx}
          >
            Assign Shift
          </Button>
        ) : null
      }
      toolbarRight={
        <>
          <ExportButton
            gridRef={gridRef}
            fileName="shift_assignments.xlsx"
            exportColumns={["employee_details.first_name", "shift_details.shift_name", "start_date", "end_date"]}
            disabled={selectedRows.length === 0}
          />
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </>
      }
    >
      <div className="list-page-grid">
        <ReusableGrid
          gridRef={gridRef}
          rowData={employeeShiftsData}
          columnDefs={columnDefs}
          paginationPageSize={pageSize}
          loading={loading}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
        />
      </div>

      <AddAssignShiftForm
        open={showModal}
        handleClose={() => setShowModal(false)}
        editData={editAssignment}
      />
    </ListPageLayout>
  );
};

export default AssignShiftList;
